import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { pairsApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { Loader } from 'components/_new/Loader';
import { MessageBox } from 'components/_new/MessageBox';
import { useProgramPanel } from 'contexts/programPanelContext';
import { EmptyState } from 'utils/emptyState';
import { dateFormat } from 'utils/dateFormat';
import { PairingMenteeQueueItem, PairingProposalItem, PairingQueueResponse } from 'types/Pairing';
import { Tag } from 'components/_new/Tag';
import { Dialog, DialogConfirm } from 'components/_new/Dialog';
import { Avatar } from 'components/_new/Avatar';
import BusinessCardIdentifier from 'components/_common/panel/BusinessCardIdentifier';
import './ProgramPairing.scss';
import { Link } from 'components/_new/Link';
import { Divider } from 'components/_new/Divider';
import { useGlobal } from 'contexts/globalContext';

const recalculateMentorStats = (items: PairingMenteeQueueItem[]): PairingMenteeQueueItem[] => {
	const mentorStats = new Map<
		number,
		{ mentorCreatedPairsCount: number; mentorCurrentProposalsCount: number; mentorReservedPairsCount: number }
	>();

	items.forEach((item) => {
		item.proposals.forEach((proposal) => {
			const current = mentorStats.get(proposal.mentorMembershipId) || {
				mentorCreatedPairsCount: 0,
				mentorCurrentProposalsCount: 0,
				mentorReservedPairsCount: 0,
			};

			if (proposal.status === 'accepted') {
				current.mentorCreatedPairsCount += 1;
			}
			if (proposal.status === 'open') {
				current.mentorCurrentProposalsCount += 1;
			}
			if (proposal.status === 'open' && proposal.reserved) {
				current.mentorReservedPairsCount += 1;
			}

			mentorStats.set(proposal.mentorMembershipId, current);
		});
	});

	return items.map((item) => ({
		...item,
		proposals: item.proposals.map((proposal) => {
			const stats = mentorStats.get(proposal.mentorMembershipId);
			if (!stats) {
				return proposal;
			}
			return {
				...proposal,
				mentorCreatedPairsCount: stats.mentorCreatedPairsCount,
				mentorCurrentProposalsCount: stats.mentorCurrentProposalsCount,
				mentorReservedPairsCount: stats.mentorReservedPairsCount,
			};
		}),
	}));
};

const isProposalAvailableForPairing = (proposal: PairingProposalItem): boolean => {
	if (proposal.status !== 'open') {
		return false;
	}

	if (proposal.mentorPairLimit === 0) {
		return false;
	}

	if (proposal.mentorPairLimit > 0 && proposal.mentorCreatedPairsCount >= proposal.mentorPairLimit) {
		return false;
	}

	return true;
};

const recalculateQueueSummary = (data: PairingQueueResponse, items: PairingMenteeQueueItem[]): PairingQueueResponse => {
	const allProposals = items.flatMap((item) => item.proposals);
	const acceptedPairsCount = allProposals.filter((proposal) => proposal.status === 'accepted').length;
	const openProposalsCount = allProposals.filter((proposal) => proposal.status === 'open').length;
	const withOpenProposalsCount = items.filter((item) =>
		item.proposals.some((proposal) => proposal.status === 'open')
	).length;
	const menteesWithCreatedPairCount = items.filter((item) =>
		item.proposals.some((proposal) => proposal.status === 'accepted')
	).length;
	const menteesWithReservedPairCount = items.filter((item) =>
		item.proposals.some((proposal) => isProposalAvailableForPairing(proposal) && proposal.reserved)
	).length;
	const menteesWithoutCreatedPairCount = items.filter((item) =>
		item.proposals.every((proposal) => proposal.status !== 'accepted')
	).length;
	const uniqueMentorIds = new Set(allProposals.map((proposal) => proposal.mentorMembershipId));
	const acceptedMentorIds = new Set(
		allProposals.filter((proposal) => proposal.status === 'accepted').map((proposal) => proposal.mentorMembershipId)
	);

	const mentorsCount = data.summary.mentorsCount ?? uniqueMentorIds.size;
	const maxLimit = data.summary.maxLimit ?? mentorsCount * 2;

	return {
		...data,
		items,
		summary: {
			...data.summary,
			acceptedPairsCount,
			openProposalsCount,
			withOpenProposalsCount,
			menteesWithCreatedPairCount,
			menteesWithReservedPairCount,
			menteesWithoutCreatedPairCount,
			mentorsCount,
			mentorsWithoutPairCount: [...uniqueMentorIds].filter((mentorId) => !acceptedMentorIds.has(mentorId)).length,
			maxLimit,
			remainingLimit: Math.max(maxLimit - acceptedPairsCount, 0),
		},
	};
};

const ProgramPairing = () => {
	const { t } = useTranslation();
	const { toastRef } = useGlobal();
	const { data: program, isSuccess: hasProgram } = useProgramPanel();
	const [pairingQueueData, setPairingQueueData] = useState<PairingQueueResponse | undefined>(undefined);
	const [pairToReject, setPairToReject] = useState<PairingProposalItem | null>(null);
	const [businessCardApplicationId, setBusinessCardApplicationId] = useState<number | null>(null);
	const [summaryFilter, setSummaryFilter] = useState<
		'withCreatedPair' | 'withReservedPair' | 'withoutCreatedPair' | 'withoutProposals' | null
	>(null);

	const pairingQueueQuery = useQuery(
		['PROGRAM_PAIRING_QUEUE', { programId: Number(program?.id) }],
		() => pairsApi.getProgramPairingQueue({ programId: Number(program?.id) }),
		{
			enabled: Boolean(hasProgram) && Boolean(program?.id),
		}
	);

	useEffect(() => {
		if (pairingQueueQuery.data) {
			setPairingQueueData(pairingQueueQuery.data);
		}
	}, [pairingQueueQuery.data]);

	const openItems = useMemo<PairingMenteeQueueItem[]>(
		() =>
			(pairingQueueData?.items || []).filter((item) =>
				item.proposals.some((proposal) => proposal.status === 'open')
			),
		[pairingQueueData?.items]
	);

	const allItems = useMemo(
		() => (pairingQueueData?.items || []).filter((item) => item.proposals.length > 0),
		[pairingQueueData?.items]
	);

	const filteredItems = useMemo(() => {
		if (!summaryFilter) {
			return allItems;
		}

		if (summaryFilter === 'withCreatedPair') {
			return allItems.filter((item) => item.proposals.some((proposal) => proposal.status === 'accepted'));
		}

		if (summaryFilter === 'withReservedPair') {
			return allItems.filter((item) =>
				item.proposals.some((proposal) => isProposalAvailableForPairing(proposal) && proposal.reserved)
			);
		}

		if (summaryFilter === 'withoutProposals') {
			return (pairingQueueData?.items || []).filter((item) => item.proposals.length === 0);
		}

		return allItems.filter((item) => item.proposals.every((proposal) => proposal.status !== 'accepted'));
	}, [allItems, pairingQueueData?.items, summaryFilter]);

	const topSummary = useMemo(() => {
		const data = pairingQueueData;
		const items = data?.items || [];
		const allProposals = items.flatMap((item) => item.proposals || []);
		const menteesWithReservedPairCount = items.filter((item) =>
			item.proposals.some((proposal) => isProposalAvailableForPairing(proposal) && proposal.reserved)
		).length;
		const uniqueMentorIds = new Set(allProposals.map((proposal) => proposal.mentorMembershipId));
		const acceptedPairsCount =
			data?.summary?.acceptedPairsCount ??
			allProposals.filter((proposal) => proposal.status === 'accepted').length;

		const mentorsWithoutPairFallback = (() => {
			const acceptedMentorIds = new Set(
				allProposals
					.filter((proposal) => proposal.status === 'accepted')
					.map((proposal) => proposal.mentorMembershipId)
			);
			return [...uniqueMentorIds].filter((mentorId) => !acceptedMentorIds.has(mentorId)).length;
		})();

		const mentorCount = data?.summary?.mentorsCount ?? uniqueMentorIds.size;
		const maxLimit = data?.summary?.maxLimit ?? mentorCount * 2;

		return {
			menteesCount: data?.summary?.allMenteesCount ?? items.length,
			menteesWithCreatedPairCount: data?.summary?.menteesWithCreatedPairCount ?? 0,
			menteesWithoutProposalsCount: data?.summary?.menteesWithoutProposalsCount ?? 0,
			menteesWithReservedPairCount,
			mentorsCount: mentorCount,
			menteesWithoutCreatedPairCount: data?.summary?.menteesWithoutCreatedPairCount ?? 0,
			maxLimit,
			remainingLimit: data?.summary?.remainingLimit ?? Math.max(maxLimit - acceptedPairsCount, 0),
			mentorsWithoutPairCount: data?.summary?.mentorsWithoutPairCount ?? mentorsWithoutPairFallback,
		};
	}, [pairingQueueData]);

	const acceptMutation = useMutation<void, Error, { pairId: number }>(
		({ pairId }: { pairId: number }) =>
			pairsApi.acceptProgramPairingProposal({ programId: Number(program?.id), pairId }),
		{
			onSuccess: (_, variables) => {
				setPairingQueueData((prev) => {
					if (!prev) {
						return prev;
					}

					const itemsWithAccepted = prev.items.map((item) => ({
						...item,
						proposals: item.proposals.map((proposal) =>
							proposal.pairId === variables.pairId
								? { ...proposal, status: 'accepted' as PairingProposalItem['status'], reserved: false }
								: proposal
						),
					}));

					const itemsWithRecalculatedMentorStats = recalculateMentorStats(itemsWithAccepted);
					return recalculateQueueSummary(prev, itemsWithRecalculatedMentorStats);
				});
				pairingQueueQuery.refetch();
				toastRef.current?.show({
					severity: 'success',
					life: 5000,
					summary: t('misc.success'),
					detail: t('programPanel.pairing.propositions.toasts.acceptSuccess'),
				});
			},
			onError: () => {
				toastRef.current?.show({
					severity: 'error',
					life: 5000,
					summary: t('misc.error'),
					detail: t('programPanel.pairing.propositions.toasts.acceptError'),
				});
			},
		}
	);

	const rejectMutation = useMutation<void, Error, { pairId: number }>(
		({ pairId }: { pairId: number }) =>
			pairsApi.rejectProgramPairingProposal({ programId: Number(program?.id), pairId }),
		{
			onSuccess: (_, variables) => {
				setPairingQueueData((prev) => {
					if (!prev) {
						return prev;
					}

					const itemsAfterRemove = prev.items
						.map((item) => ({
							...item,
							proposals: item.proposals.filter((proposal) => proposal.pairId !== variables.pairId),
						}))
						.filter((item) => item.proposals.length > 0);

					const itemsWithRecalculatedMentorStats = recalculateMentorStats(itemsAfterRemove);
					return recalculateQueueSummary(prev, itemsWithRecalculatedMentorStats);
				});
				setPairToReject(null);
				toastRef.current?.show({
					severity: 'success',
					life: 5000,
					summary: t('misc.success'),
					detail: t('programPanel.pairing.propositions.toasts.removeSuccess'),
				});
			},
			onError: () => {
				toastRef.current?.show({
					severity: 'error',
					life: 5000,
					summary: t('misc.error'),
					detail: t('programPanel.pairing.propositions.toasts.removeError'),
				});
			},
		}
	);

	const reserveMutation = useMutation<{ reserved: boolean }, Error, { pairId: number; reserved: boolean }>(
		({ pairId }: { pairId: number; reserved: boolean }) =>
			pairsApi.reserveProgramPairingProposal({ programId: Number(program?.id), pairId }),
		{
			onSuccess: (data, variables) => {
				setPairingQueueData((prev) => {
					if (!prev) {
						return prev;
					}

					const updatedItems = prev.items.map((item) => ({
						...item,
						proposals: item.proposals.map((proposal) =>
							proposal.pairId === variables.pairId ? { ...proposal, reserved: data.reserved } : proposal
						),
					}));

					const itemsWithRecalculatedMentorStats = recalculateMentorStats(updatedItems);
					return recalculateQueueSummary(prev, itemsWithRecalculatedMentorStats);
				});
				toastRef.current?.show({
					severity: 'success',
					life: 5000,
					summary: t('misc.success'),
					detail: data.reserved
						? t('programPanel.pairing.propositions.toasts.reserveSuccess')
						: t('programPanel.pairing.propositions.toasts.cancelReserveSuccess'),
				});
			},
			onError: (_, variables) => {
				toastRef.current?.show({
					severity: 'error',
					life: 5000,
					summary: t('misc.error'),
					detail: variables.reserved
						? t('programPanel.pairing.propositions.toasts.cancelReserveError')
						: t('programPanel.pairing.propositions.toasts.reserveError'),
				});
			},
		}
	);

	const handleAccept = (proposal: PairingProposalItem) => {
		acceptMutation.mutate({ pairId: proposal.pairId });
	};

	const handleReject = (proposal: PairingProposalItem) => {
		setPairToReject(proposal);
	};

	const confirmReject = () => {
		if (!pairToReject) {
			return;
		}
		rejectMutation.mutate({ pairId: pairToReject.pairId });
	};

	const handleOpenBusinessCard = (applicationId: number) => {
		setBusinessCardApplicationId(applicationId);
	};

	const handleReserve = (proposal: PairingProposalItem) => {
		reserveMutation.mutate({ pairId: proposal.pairId, reserved: proposal.reserved });
	};

	const clearSummaryFilter = () => {
		setSummaryFilter(null);
	};

	const getApplicationPath = (applicationId: number): string | undefined =>
		program?.name ? `/program/${program.name}/application/${applicationId}` : undefined;
	const getPairPath = (pairId: number): string | undefined =>
		program?.name ? `/program/${program.name}/pair/${pairId}` : undefined;

	const activeSummaryFilterLabel = useMemo(() => {
		if (summaryFilter === 'withCreatedPair') {
			return t('programPanel.pairing.propositions.summary.menteesWithCreatedPairCount');
		}

		if (summaryFilter === 'withReservedPair') {
			return t('programPanel.pairing.propositions.summary.menteesWithReservedPairCount');
		}

		if (summaryFilter === 'withoutCreatedPair') {
			return t('programPanel.pairing.propositions.summary.menteesWithoutCreatedPairCount');
		}

		if (summaryFilter === 'withoutProposals') {
			return t('programPanel.pairing.propositions.summary.menteesWithoutProposalsCount');
		}

		return null;
	}, [summaryFilter, t]);

	return (
		<>
			<Helmet title={t('programPanel.pairing.propositions.title')} />
			<SubPageTitle
				title={t('programPanel.pairing.propositions.title')}
				additional={program?.displayName}
				right={
					<Button
						label={t('actions.refresh')}
						icon="refresh"
						iconSet="pi"
						onClick={() => pairingQueueQuery.refetch()}
						loading={pairingQueueQuery.isFetching}
					/>
				}
			/>

			<Dialog
				title={t('userPanel.pairing.personalDetails')}
				visible={businessCardApplicationId !== null}
				onHide={() => setBusinessCardApplicationId(null)}
				size="lg"
				className="p-dialog-buissnescard"
			>
				{businessCardApplicationId !== null && (
					<BusinessCardIdentifier
						visible={businessCardApplicationId !== null}
						scope="program-admin"
						programId={Number(program?.id)}
						applicationId={businessCardApplicationId}
					/>
				)}
			</Dialog>
			<Box variant="white-bordered">
				<BoxSection>
					{pairingQueueQuery.isLoading && (
						<div className="flex justify-content-center">
							<Loader />
						</div>
					)}
					{pairingQueueQuery.isSuccess && (
						<Box variant="blue-bordered">
							<BoxSection>
								<div className="grid">
									<div className="col-12 md:col-4 flex flex-column gap-2">
										<div>
											{t('programPanel.pairing.propositions.summary.menteesCount')}:{' '}
											<span className="text-primary font-bold">{topSummary.menteesCount}</span>
										</div>
										<div>
											{t('programPanel.pairing.propositions.summary.mentorsCount')}:{' '}
											<span className="text-primary font-bold">{topSummary.mentorsCount}</span>
										</div>
									</div>
									<div className="col-12 md:hidden">
										<Divider />
									</div>
									<div className="col-12 md:col-4 flex flex-column gap-2">
										<div>
											{t('programPanel.pairing.propositions.summary.menteesWithCreatedPairCount')}
											:{' '}
											<button
												type="button"
												onClick={() => setSummaryFilter('withCreatedPair')}
												className="text-primary font-bold cursor-pointer border-none bg-transparent p-0 text-underline"
											>
												{topSummary.menteesWithCreatedPairCount}
											</button>
										</div>
										<div>
											{t(
												'programPanel.pairing.propositions.summary.menteesWithoutCreatedPairCount'
											)}
											:{' '}
											<button
												type="button"
												onClick={() => setSummaryFilter('withoutCreatedPair')}
												className="text-primary font-bold cursor-pointer border-none bg-transparent p-0 text-underline"
											>
												{topSummary.menteesWithoutCreatedPairCount}
											</button>
										</div>
										<div>
											{t(
												'programPanel.pairing.propositions.summary.menteesWithoutProposalsCount'
											)}
											:{' '}
											<span className="text-primary font-bold">
												{topSummary.menteesWithoutProposalsCount}
											</span>
										</div>
										<div>
											{t(
												'programPanel.pairing.propositions.summary.menteesWithReservedPairCount'
											)}
											:{' '}
											<button
												type="button"
												onClick={() => setSummaryFilter('withReservedPair')}
												className="text-primary font-bold cursor-pointer border-none bg-transparent p-0 text-underline"
											>
												{topSummary.menteesWithReservedPairCount}
											</button>
										</div>
									</div>
									<div className="col-12 md:hidden">
										<Divider />
									</div>
									<div className="col-12 md:col-4 flex flex-column gap-2">
										<div>
											{t('programPanel.pairing.propositions.summary.maxLimit')}:{' '}
											<span className="text-primary font-bold">{topSummary.maxLimit}</span>
										</div>
										<div>
											{t('programPanel.pairing.propositions.summary.remainingLimit')}:{' '}
											<span className="text-primary font-bold">{topSummary.remainingLimit}</span>
										</div>
										<div>
											{t('programPanel.pairing.propositions.summary.mentorsWithoutPairCount')}:{' '}
											<span className="text-primary font-bold">
												{topSummary.mentorsWithoutPairCount}
											</span>
										</div>
									</div>
								</div>
							</BoxSection>
						</Box>
					)}

					{pairingQueueQuery.isSuccess && openItems.length === 0 && (
						<EmptyState title={t('programPanel.pairing.propositions.empty')} />
					)}
					{pairingQueueQuery.isError && (
						<MessageBox
							variant="warn"
							title={t('programPanel.pairing.propositions.error.title')}
							message={t('programPanel.pairing.propositions.error.message')}
						/>
					)}

					{summaryFilter && (
						<div className="mb-2 flex justify-content-end align-items-center gap-2">
							{activeSummaryFilterLabel && (
								<span className="font-medium">{activeSummaryFilterLabel}</span>
							)}
							<Button
								label={t('actions.cancel')}
								variant="primary-outlined"
								onClick={clearSummaryFilter}
							/>
						</div>
					)}

					{summaryFilter && filteredItems.length === 0 && (
						<EmptyState title={t('programPanel.pairing.propositions.empty')} />
					)}

					{filteredItems.length > 0 && (
						<div className="flex flex-column gap-2">
							{filteredItems.map((item) => (
								<Box
									key={item.menteeApplicationId}
									variant="white-bordered"
									vertical
									className="program-pairing-row flex-column lg:flex-row"
								>
									<BoxSection className="max-w-300px" sticky={{ offsetTop: 16, offsetBottom: 16 }}>
										<div className="flex flex-row gap-2">
											<Avatar src={item.menteeAvatar} name={item.menteeDisplayName} size="lg" />
											<div>
												<div className="flex align-items-center gap-2">
													<Link
														to={{
															pathname:
																getApplicationPath(item.menteeApplicationId) || '#',
														}}
														className="font-bold link"
														preventDefaultOnClick
														onClick={() => {
															handleOpenBusinessCard(item.menteeApplicationId);
														}}
													>
														{item.menteeDisplayName}
													</Link>
													{getApplicationPath(item.menteeApplicationId) && (
														<Link
															to={{
																pathname: getApplicationPath(item.menteeApplicationId),
															}}
															target="_blank"
															rel="noreferrer"
															aria-label={item.menteeDisplayName}
															icon="external-link"
															iconClassName="text-sm"
															iconOnly
															label={t('misc.openInNewTab')}
															variant="standard"
														/>
													)}
												</div>
												<div className="text-gray">
													{item.menteePosition || '-'} · {item.menteeDepartment || '-'}
												</div>
											</div>
										</div>
										<div>
											<b>{t('programPanel.pairing.propositions.labels.developmentAreas')}</b>
											<div className="flex flex-row flex-wrap gap-1 mt-2">
												{item.menteeAreas.map((area) => (
													<Tag
														key={`${item.menteeApplicationId}-${area}`}
														variant="blue"
														bold
														rounded
													>
														{area}
													</Tag>
												))}
											</div>
										</div>

										{item.menteeNeeds && (
											<div>
												<b>{t('programPanel.pairing.propositions.menteeNeeds')}</b>
												<div className="mt-2">{item.menteeNeeds}</div>
											</div>
										)}
									</BoxSection>

									<BoxSection className="flex-1">
										{item.proposals.map((proposal) => {
											const isAcceptLoading =
												acceptMutation.isPending &&
												acceptMutation.variables?.pairId === proposal.pairId;
											const isRejectLoading =
												rejectMutation.isPending &&
												rejectMutation.variables?.pairId === proposal.pairId;
											const isReserveLoading =
												reserveMutation.isPending &&
												reserveMutation.variables?.pairId === proposal.pairId;
											const isAnyActionLoading =
												acceptMutation.isPending ||
												rejectMutation.isPending ||
												reserveMutation.isPending;
											const isOpen = proposal.status === 'open';
											const isReserved = proposal.reserved;
											const withGreenBorder = proposal.status === 'accepted';
											const isLimitZero = proposal.mentorPairLimit === 0;
											const isLimitReached =
												proposal.mentorPairLimit > 0 &&
												proposal.mentorCreatedPairsCount >= proposal.mentorPairLimit;
											const rejectedByMentee = Boolean(proposal.rejectedByMentee);
											const rejectedByMentor = Boolean(proposal.rejectedByMentor);
											const acceptedByBothSides =
												Boolean(proposal.acceptedByMentee) &&
												Boolean(proposal.acceptedByMentor);
											const pairCreatedAtDate =
												acceptedByBothSides &&
												proposal.acceptedByMentee &&
												proposal.acceptedByMentor
													? new Date(
															Math.max(
																new Date(proposal.acceptedByMentee).getTime(),
																new Date(proposal.acceptedByMentor).getTime()
															)
													  )
													: null;
											const pairCreatedAt = pairCreatedAtDate
												? dateFormat(pairCreatedAtDate, 'date')
												: undefined;
											const withRedBorder =
												!withGreenBorder &&
												(proposal.status === 'rejected' ||
													rejectedByMentee ||
													rejectedByMentor ||
													isLimitZero ||
													isLimitReached);
											const shouldHidePrimaryActions =
												rejectedByMentee ||
												rejectedByMentor ||
												isLimitZero ||
												isLimitReached ||
												acceptedByBothSides;
											const shouldShowStatusInfo =
												(shouldHidePrimaryActions || isOpen) &&
												Boolean(getPairPath(proposal.pairId));

											const statusInfoText = (() => {
												if (rejectedByMentee) {
													return t(
														'programPanel.pairing.propositions.statusInfo.rejectedByMentee'
													);
												}
												if (rejectedByMentor) {
													return t(
														'programPanel.pairing.propositions.statusInfo.rejectedByMentor'
													);
												}
												if (acceptedByBothSides) {
													return pairCreatedAt
														? t(
																'programPanel.pairing.propositions.statusInfo.pairCreatedAt',
																{
																	date: pairCreatedAt,
																}
														  )
														: t(
																'programPanel.pairing.propositions.statusInfo.acceptedByBothSides'
														  );
												}
												if (isLimitZero || isLimitReached) {
													return t(
														'programPanel.pairing.propositions.statusInfo.limitReached'
													);
												}
												if (isOpen) {
													return t(
														'programPanel.pairing.propositions.statusInfo.generatedAndVisibleForMentee'
													);
												}
												return '';
											})();

											const hasScoreInfo =
												proposal.score.score !== null ||
												(proposal.score.comment !== null &&
													proposal.score.comment.trim() !== '');

											return (
												<Box
													key={proposal.pairId}
													variant="white-bordered"
													className={classNames(
														withGreenBorder && 'border-3 border-green-300 bg-green-50/50',
														withRedBorder && 'border-3 border-red-300 bg-red-50/50'
													)}
												>
													<BoxSection>
														<div className="flex flex-row gap-2 align-items-start">
															<div className="flex flex-column gap-1">
																<div className="flex flex-row gap-2">
																	<Avatar
																		src={proposal.mentorAvatar}
																		name={proposal.mentorDisplayName}
																		size="lg"
																	/>
																	<div className="flex flex-column gap-1">
																		<div className="flex align-items-center gap-2">
																			<Link
																				to={{
																					pathname:
																						getApplicationPath(
																							proposal.mentorApplicationId
																						) || '#',
																				}}
																				className="font-bold link"
																				preventDefaultOnClick
																				onClick={(event) => {
																					handleOpenBusinessCard(
																						proposal.mentorApplicationId
																					);
																				}}
																			>
																				{proposal.mentorDisplayName}
																			</Link>

																			{getApplicationPath(
																				proposal.mentorApplicationId
																			) && (
																				<Link
																					to={{
																						pathname: getApplicationPath(
																							proposal.mentorApplicationId
																						),
																					}}
																					icon="external-link"
																					iconClassName="text-sm"
																					iconOnly
																					label={t('misc.openInNewTab')}
																					target="_blank"
																					rel="noreferrer"
																					aria-label={
																						proposal.mentorDisplayName
																					}
																				/>
																			)}
																		</div>
																		<div className="text-gray">
																			{proposal.mentorPosition || '-'} ·{' '}
																			{proposal.mentorDepartment || '-'}
																		</div>
																	</div>
																</div>

																<div className="inline-block text-gray mt-2 mb-2">
																	{t(
																		'programPanel.pairing.propositions.labels.limit'
																	)}
																	: {proposal.mentorPairLimit},{' '}
																	{t(
																		'programPanel.pairing.propositions.labels.created'
																	)}
																	: {proposal.mentorCreatedPairsCount},{' '}
																	{t(
																		'programPanel.pairing.propositions.labels.proposals'
																	)}
																	: {proposal.mentorCurrentProposalsCount},{' '}
																	{t(
																		'programPanel.pairing.propositions.labels.reserved'
																	)}
																	: {proposal.mentorReservedPairsCount}
																</div>
															</div>
															{hasScoreInfo && (
																<div className="program-pairing-match-note">
																	<div className="program-pairing-score">
																		{proposal.score.score ?? '-'}
																		{proposal.score.score !== null && (
																			<span className="ml-1 text-sm font-normal">
																				{t(
																					'programPanel.pairing.propositions.labels.matchPercent'
																				)}
																			</span>
																		)}
																	</div>
																	<div className="program-pairing-match-list">
																		{proposal.score.comment ?? '-'}
																	</div>
																</div>
															)}
														</div>

														{Boolean(proposal.mentorAreas.length) && (
															<div className="flex flex-row flex-wrap gap-1">
																{proposal.mentorAreas.map((area) => (
																	<Tag
																		key={`${proposal.pairId}-${area}`}
																		variant="blue"
																		bold
																		rounded
																	>
																		{area}
																	</Tag>
																))}
															</div>
														)}
													</BoxSection>
													<BoxSection>
														<div className="program-pairing-proposal-actions flex justify-content-between align-items-center gap-2">
															<div className="flex flex-column">
																{shouldShowStatusInfo && (
																	<>
																		<div className="font-semibold">
																			{statusInfoText}
																		</div>
																		<Link
																			to={{
																				pathname:
																					getPairPath(proposal.pairId) || '#',
																			}}
																			className="font-semibold"
																		>
																			{t(
																				'programPanel.pairing.propositions.statusInfo.pairDetails'
																			)}
																		</Link>
																	</>
																)}
															</div>

															<div className="flex gap-2">
																{!shouldHidePrimaryActions && (
																	<>
																		<Button
																			label={t(
																				'programPanel.pairing.propositions.actions.acceptAndCreate'
																			)}
																			variant="primary"
																			onClick={() => handleAccept(proposal)}
																			loading={isAcceptLoading}
																			disabled={
																				isAnyActionLoading ||
																				!isOpen ||
																				withRedBorder
																			}
																		/>
																		<Button
																			label={
																				isReserved
																					? t(
																							'programPanel.pairing.propositions.actions.cancelReserve'
																					  )
																					: t(
																							'programPanel.pairing.propositions.actions.reserve'
																					  )
																			}
																			variant="primary-outlined"
																			onClick={() => handleReserve(proposal)}
																			loading={isReserveLoading}
																			disabled={isAnyActionLoading || !isOpen}
																		/>
																	</>
																)}

																{(!shouldHidePrimaryActions || withRedBorder) && (
																	<Button
																		label={t(
																			'programPanel.pairing.propositions.actions.remove'
																		)}
																		variant="danger-text"
																		onClick={() => handleReject(proposal)}
																		loading={isRejectLoading}
																		disabled={
																			isAnyActionLoading ||
																			(!isOpen && !withRedBorder)
																		}
																	/>
																)}
															</div>
														</div>
													</BoxSection>
												</Box>
											);
										})}
									</BoxSection>
								</Box>
							))}
						</div>
					)}
				</BoxSection>
			</Box>
			<DialogConfirm
				visible={Boolean(pairToReject)}
				onHide={() => setPairToReject(null)}
				onConfirm={confirmReject}
				message={t('programPanel.pairing.removeApplication.reassurance')}
			/>
		</>
	);
};

export default ProgramPairing;
